import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import type { UIHashKey } from "@/features/auth/types/hashKeys.types";
import { formatTokenDate } from "@/lib/dateUtils";

interface TeamListRowProps {
  member: UIHashKey;
  isSelected: boolean;
  onSelect: () => void;
  onEdit: (keyId: string) => void;
  onDelete: (keyId: string) => void;
  onCopyHash?: (hashKey: string) => void;
  isDeleting?: boolean;
}

export function TeamListRow({
  member,
  isSelected,
  onSelect,
  onEdit,
  onDelete,
  onCopyHash,
  isDeleting,
}: TeamListRowProps) {
  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit(member.id);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(member.id);
  };

  const handleCopyHash = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCopyHash?.(member.hashKey);
  };

  return (
    <div className="flex h-[76px] items-center gap-4 rounded-lg bg-white p-5 transition-colors duration-200 hover:bg-gray-50">
      {/* Checkbox */}
      <Checkbox
        checked={isSelected}
        onCheckedChange={onSelect}
        className="border-gray-400"
        onClick={(e) => e.stopPropagation()}
      />

      {/* Name */}
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <p className="max-w-[90%] truncate text-sm leading-6 font-bold -tracking-wide text-neutral-950">
            {member.name}
          </p>
          <div
            className={cn(
              "h-2 w-2 flex-shrink-0 rounded-full",
              member.active ? "bg-green-500" : "bg-red-500",
            )}
          />
        </div>
      </div>

      {/* Date Created - hide on small screens, remove min-width */}
      <div className="hidden min-w-0 flex-1 lg:block">
        <p className="max-w-[400px] truncate text-sm leading-6 font-medium -tracking-wide text-neutral-950">
          {formatTokenDate(member.dateCreated)}
        </p>
      </div>

      {/* Last Used - hide on medium screens, remove min-width */}
      <div className="hidden min-w-0 flex-1 xl:block">
        <p className="max-w-[400px] truncate text-sm leading-6 font-medium -tracking-wide text-neutral-950">
          {member.lastUsed ? formatTokenDate(member.lastUsed) : "Never"}
        </p>
      </div>

      {/* Hash with Copy Button - remove min-width */}
      <div className="min-w-0 flex-[2]">
        <div className="flex items-center gap-2">
          <p className="max-w-[160px] truncate text-sm leading-6 font-medium text-neutral-950">
            {member.hashKey}
          </p>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopyHash}
            className="h-7 w-7 bg-neutral-100 p-0 hover:bg-gray-100"
            title="Copy hash key"
          >
            <i className="ri-file-copy-line text-muted text-xs"></i>
          </Button>
        </div>
      </div>

      {/* Actions - make more compact on mobile */}
      <div className="flex min-w-[80px] flex-shrink-0 justify-end gap-2 sm:min-w-[120px]">
        <Button
          className="gap-2 p-1.5 text-xs leading-normal font-bold tracking-normal text-neutral-800 hover:bg-neutral-100 hover:text-neutral-800"
          variant="minimal"
          icon
          onClick={handleEdit}
        >
          <i className="ri-edit-line text-md font-light"></i>
          <span className="hidden sm:inline">Edit</span>
        </Button>
        <Button
          className="bg-destructive hover:bg-destructive/60 gap-2 p-1.5 text-xs font-medium text-white hover:text-white"
          variant="minimal"
          icon
          onClick={handleDelete}
          disabled={isDeleting}
        >
          {isDeleting ? (
            <Spinner size="sm" className="stroke-white!" />
          ) : (
            <i className="ri-delete-bin-line text-md"></i>
          )}
          <span className="hidden sm:inline">Deactivate</span>
        </Button>
      </div>
    </div>
  );
}
